import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import "./AdminKabiles.css";

interface DeletedKabile {
  _id: string;
  name: string;
  members: any[];
  isDeleted: boolean;
  createdAt: string;
  founder?: string;
}

interface CleanupResult {
  success: boolean;
  dryRun: boolean;
  kabileCount: number;
  totalRemoved: {
    kabileAnswersCount: number;
    kabileSelectedAnswersCount: number;
    globalVotesCount: number;
    userReferencesCount: number;
    invitationsCount: number;
    memberRequestsCount: number;
    notificationsCount: number;
    cacheInvalidatedCount: number;
    physicallyDeletedCount: number;
  };
}

export default function AdminKabiles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [deletedKabiles, setDeletedKabiles] = useState<DeletedKabile[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    deleted: 0,
    empty: 0
  });
  
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiGet<{
        ok: true;
        stats: typeof stats;
        deletedKabiles: DeletedKabile[];
      }>("/api/admin/kabile/stats");
      
      setStats(response.stats);
      setDeletedKabiles(response.deletedKabiles);
    } catch (error) {
      console.error("Error loading kabile data:", error);
      alert("Kabile verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = async () => {
    try {
      setCleanupLoading(true);
      const response = await apiPost<CleanupResult>(
        "/api/admin/kabile/cleanup-deleted",
        { dryRun: true }
      );
      setCleanupResult(response);
    } catch (error) {
      console.error("Dry-run failed:", error);
      alert("Ön izleme başarısız oldu");
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleRealCleanup = async () => {
    if (!window.confirm("⚠️ 0 üyeli tüm silinmiş kabileler kalıcı olarak silinecektir. Devam etmek istediğiniz emin misiniz?")) {
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await apiPost<CleanupResult>(
        "/api/admin/kabile/cleanup-deleted",
        { dryRun: false }
      );
      setCleanupResult(response);
      
      // Verileri yenile
      await loadData();
      setShowConfirm(false);
    } catch (error) {
      console.error("Cleanup failed:", error);
      alert("Silme işlemi başarısız oldu");
    } finally {
      setCleanupLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-kabiles-container">Yükleniyor...</div>;
  }

  return (
    <div className="admin-kabiles-container">
      <h1>🏛️ Kabile Yönetimi</h1>
      
      {/* İstatistikler */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Toplam Kabile</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Aktif Kabile</div>
        </div>
        <div className="stat-card deleted">
          <div className="stat-value">{stats.deleted}</div>
          <div className="stat-label">Silinmiş Kabile</div>
        </div>
        <div className="stat-card empty">
          <div className="stat-value">{stats.empty}</div>
          <div className="stat-label">0 Üyeli (Silinebilir)</div>
        </div>
      </div>

      {/* Silinmiş Kabileler Listesi */}
      <div className="section">
        <h2>❌ Silinmiş Kabileler ({deletedKabiles.length})</h2>
        {deletedKabiles.length === 0 ? (
          <p className="info-text">Silinmiş kabile yok</p>
        ) : (
          <div className="kabile-table">
            <table>
              <thead>
                <tr>
                  <th>Adı</th>
                  <th>Üye Sayısı</th>
                  <th>Durum</th>
                  <th>Oluşturulma Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {deletedKabiles.map((kabile) => (
                  <tr key={kabile._id} className={kabile.members.length === 0 ? "empty-row" : ""}>
                    <td className="name">{kabile.name}</td>
                    <td className="members">{kabile.members.length}</td>
                    <td className="status">
                      {kabile.members.length === 0 ? (
                        <span className="badge empty">🗑️ Silinebilir</span>
                      ) : (
                        <span className="badge warning">⚠️ {kabile.members.length} üye var</span>
                      )}
                    </td>
                    <td className="date">
                      {new Date(kabile.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cleanup Kontrol */}
      <div className="section cleanup-section">
        <h2>🧹 Cleanup İşlemi</h2>
        <p className="info-text">
          {stats.empty} adet 0 üyeli kabile fiziksel olarak silinebilir.
        </p>
        
        <div className="cleanup-buttons">
          <button 
            onClick={handleDryRun}
            disabled={cleanupLoading || stats.empty === 0}
            className="btn btn-preview"
          >
            {cleanupLoading ? "⏳ Kontrol ediliyor..." : "👁️ Ön İzleme"}
          </button>
          
          {stats.empty > 0 && (
            <>
              {!showConfirm ? (
                <button 
                  onClick={() => setShowConfirm(true)}
                  disabled={cleanupLoading}
                  className="btn btn-danger"
                >
                  🗑️ {stats.empty} Kabilenin Silinmesini Onayla
                </button>
              ) : (
                <>
                  <button 
                    onClick={handleRealCleanup}
                    disabled={cleanupLoading}
                    className="btn btn-destroy"
                  >
                    {cleanupLoading ? "⏳ Siliniyor..." : "✓ Evet, Sil"}
                  </button>
                  <button 
                    onClick={() => setShowConfirm(false)}
                    disabled={cleanupLoading}
                    className="btn btn-cancel"
                  >
                    ✕ İptal
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Cleanup Sonuçları */}
        {cleanupResult && (
          <div className={`cleanup-result ${cleanupResult.success ? 'success' : 'warning'}`}>
            <h3>
              {cleanupResult.dryRun ? "📋 Ön İzleme Sonuçları" : "✅ Silme Tamamlandı"}
            </h3>
            
            <div className="result-stats">
              <div className="result-row">
                <span>İşlem Yapılan Kabile Sayısı:</span>
                <strong>{cleanupResult.kabileCount}</strong>
              </div>
              <div className="result-row">
                <span>Fiziksel Olarak Silinen Kabileler:</span>
                <strong>{cleanupResult.totalRemoved.physicallyDeletedCount}</strong>
              </div>
              <div className="result-row">
                <span>Silinen Notificationlar:</span>
                <strong>{cleanupResult.totalRemoved.notificationsCount}</strong>
              </div>
              <div className="result-row">
                <span>Güncellenen Kullanıcılar:</span>
                <strong>{cleanupResult.totalRemoved.userReferencesCount}</strong>
              </div>
              <div className="result-row">
                <span>Silinen Cache Entry'ler:</span>
                <strong>{cleanupResult.totalRemoved.cacheInvalidatedCount}</strong>
              </div>
            </div>

            {cleanupResult.dryRun && (
              <div className="info-box">
                ℹ️ Bu ön izlemedir. Gerçek silme için "Evet, Sil" butonuna tıklayın.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bilgi Kutusu */}
      <div className="info-box">
        <h3>ℹ️ Cleanup İşlemi Nedir?</h3>
        <ul>
          <li>0 üyeli (boş) silinmiş kabileleri fiziksel olarak siler</li>
          <li>İlişkili tüm verileri (notifications, cache) temizler</li>
          <li>Kabile isimlerini tekrar kullanılabilir hale getirir</li>
          <li>Ön izleme ile ne silineceğini önceden görebilirsiniz</li>
        </ul>
      </div>
    </div>
  );
}
